// 信令 WebSocket 协议 — 完整镜像 LocalSend signaling.rs
// 来源: localsend-main/packages/core/src/webrtc/signaling.rs
//
// 与原生 LocalSend 的唯一差异：
// - 原 LocalSend 按 IP 分组（同公网 IP 视为同房间），快船改用显式 `room` 参数（支持跨网络）
// - WS URL: wss://api.kuaichuan.pages.dev/api/signaling/ws?room=<roomId>&d=<base64url(JSON(ClientInfoWithoutId))>
//
// 消息字段命名保持完全一致（type 用 SCREAMING_SNAKE_CASE，其余字段用 camelCase），
// 便于未来与原生 LocalSend APP 互通。

import type { ClientInfo, ClientInfoWithoutId } from './device.js';

// ===== Server → Client 消息 =====
// 来源: WsServerMessage (signaling.rs:21-61)

export type WsServerMessage =
  | { type: 'HELLO'; client: ClientInfo; peers: ClientInfo[] }
  | { type: 'JOIN'; peer: ClientInfo }
  | { type: 'UPDATE'; peer: ClientInfo }
  | { type: 'LEFT'; peerId: string }
  | { type: 'OFFER'; peer: ClientInfo; sessionId: string; sdp: string }
  | { type: 'ANSWER'; peer: ClientInfo; sessionId: string; sdp: string }
  | { type: 'ERROR'; code: number };

// ===== Client → Server 消息 =====
// 来源: WsClientMessage (signaling.rs:154-160)

export type WsClientMessage =
  | { type: 'UPDATE'; info: ClientInfoWithoutId }
  | { type: 'OFFER'; sessionId: string; target: string; sdp: string }
  | { type: 'ANSWER'; sessionId: string; target: string; sdp: string };

// ===== SDP 消息辅助 =====
// 来源: WsClientSdpMessage / WsServerSdpMessage (signaling.rs:63-75, 162-175)
// sdp 字段为 zlib 压缩 + base64url 无 padding 编码后的字符串

// 错误码（与 HTTP 状态码语义一致，便于客户端处理）
export const WsErrorCode = {
  TOO_MANY_REQUESTS: 429,
  GONE: 410,
  BAD_REQUEST: 400,
  INTERNAL: 500,
  LIMIT_REACHED: 503,
} as const;

// WS 连接 URL 构造（前端用）
export function buildWsUrl(workerOrigin: string, roomId: string, info: ClientInfoWithoutId): string {
  const json = JSON.stringify(info);
  // base64url 无 padding
  const b64 = base64UrlEncode(json);
  const wsOrigin = workerOrigin.replace(/^http/, 'ws');
  return `${wsOrigin}/api/signaling/ws?room=${encodeURIComponent(roomId)}&d=${b64}`;
}

// base64url 编码（无 padding），同时支持浏览器和 Worker 环境
export function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  // 浏览器/Worker 通用：先用 btoa 处理二进制字符串
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const b64 = (globalThis as any).btoa(bin) as string;
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '='.repeat(4 - (input.length % 4));
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/') + pad;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bin = (globalThis as any).atob(b64) as string;
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}
